import React, { FC } from 'react';
import classNames from 'classnames';
import { Hyperlink } from '../Hyperlink';
import { MaterialIcon } from '../material-icon/MaterialIcon';
import styles from './breadcrumb.module.scss';

const Link: FC<{
  url: string;
  title: string;
  linkAttributes?: Record<string, string>;
}> = ({ url, title, linkAttributes }) => {
  return (
    <Hyperlink
      href={url}
      className={classNames(styles.text, styles.link)}
      title={title}
      {...(linkAttributes ?? {})}
    >
      {title}
    </Hyperlink>
  );
};

const Span: FC<{
  title: string;
}> = ({ title }) => {
  return (
    <span className={classNames(styles.text, styles.defaultCursor)}>
      {title}
    </span>
  );
};

export type BreadcrumbType = Array<{
  url: string;
  label: string;
  current?: boolean;
  subBreadcrumbs?: Array<{ url: string; label: string; current?: boolean }>;
}>;

type Props = {
  breadcrumbs: BreadcrumbType;
  shortVersion?: boolean;
  linkAttributes?: Record<string, string>;
};

/**
 * The breadcrumb is common object in the seo world, please follow this guidelines
 * when you want to include it into the website
 * @see https://www.notion.so/doctrine/Breadcrumb-4abf337c87b94fd38066a3933bcdb525
 */
export const Breadcrumb: FC<Props> = ({
  breadcrumbs = [],
  shortVersion = false,
  linkAttributes,
}) => {
  return (
    <ol className={styles.breadcrumbs}>
      {breadcrumbs.map(({ url, label, current, subBreadcrumbs = [] }) => (
        <li
          key={url}
          data-test-id={url}
          className={classNames(styles.item, {
            [styles.subListParent]: subBreadcrumbs.length,
            [styles.ellipsisText]: shortVersion,
          })}
        >
          {!current ? (
            <Link url={url} title={label} linkAttributes={linkAttributes} />
          ) : (
            <Span title={label} />
          )}
          {subBreadcrumbs.length ? (
            <ul className={styles.subListContainer}>
              {subBreadcrumbs.map((item, index) => (
                <div key={item.url} className={styles.subItemContainer}>
                  {index !== 0 ? (
                    <MaterialIcon
                      name="subdirectory_arrow_right"
                      className={styles.icon}
                    />
                  ) : null}
                  <li className={styles.subItem}>
                    <Link
                      url={item.url}
                      title={item.label}
                      linkAttributes={linkAttributes}
                    />
                  </li>
                </div>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ol>
  );
};
